import {
  computed, inject, onMounted, onUnmounted, provide, ref,
} from 'vue';

/**
 * Shell context of the navigation (E-0102 / S-0012).
 *
 * The XS/SM presentation (accordions in flow, full-screen period modal) must
 * follow the SHELL the navigation is mounted in, not a second breakpoint
 * computation: the hosts pick the bottom drawer on `useDisplay().mobile` while
 * the navigation used to read `smAndDown` on its own. Both thresholds coincide
 * today (`mobileBreakpoint: 'md'`), but nothing guarantees they keep doing so.
 *
 * `NavDrawerShell` (the shell extracted in S-0030, used by `NavBottomDrawer`
 * and the grid dashboards) declares the drawer shell; `KpDashboardNavigation`
 * prefers it and falls back to its own breakpoint when mounted outside a
 * drawer.
 */
export const NAV_DRAWER_SHELL_KEY = Symbol('kpNavDrawerShell');

export function provideNavDrawerShell() {
  provide(NAV_DRAWER_SHELL_KEY, true);
}

/**
 * @returns {boolean} true when the navigation is mounted inside the bottom drawer.
 */
export function useNavDrawerShell() {
  return inject(NAV_DRAWER_SHELL_KEY, false);
}

/**
 * Number of drawer shells currently mounted. A count and not a boolean: nothing
 * guarantees that a page leaving the screen unmounts its shell before the next page
 * mounts its own.
 */
const mountedNavDrawerShells = ref(0);

/**
 * Declares a mounted drawer shell for the lifetime of the calling component.
 *
 * The drawer is FIXED at the bottom of the viewport (`NavBotBar`) and takes no room in
 * the layout: `DefaultLayout` reads this presence to reserve the folded drawer height
 * under `v-main`, so that no page has to reserve it on its own.
 */
export function registerNavDrawerShell() {
  onMounted(() => {
    mountedNavDrawerShells.value += 1;
  });
  onUnmounted(() => {
    mountedNavDrawerShells.value -= 1;
  });
}

/**
 * @returns {import('vue').ComputedRef<boolean>} true while at least one drawer shell
 *   is mounted.
 */
export function useIsNavDrawerShellMounted() {
  return computed(() => mountedNavDrawerShells.value > 0);
}
